import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import PillBadge from './PillBadge';

const ServicesSection = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  const services = [
    {
      icon: "⏱️",
      title: "Quick Turnaround Time",
      description: "Ensuring fastest delivery possible, all projects are sure to meet the deadline."
    },
    {
      icon: "⚡",
      title: "Efficient Workflow",
      description: "Over 16 years’ experience ensures fast, reliable delivery—always meeting deadlines without losing quality."
    },
    {
      icon: "🤝",
      title: "Collaborative Process",
      description: "Collaboration is at the heart of every project, drawing on client feedback to achieve results that impress."
    },
    {
      icon: "🔍",
      title: "Attention to Detail",
      description: "Every edit and design is meticulously crafted for a polished, standout finish."
    },
    {
      icon: "👥",
      title: "Professional Team",
      description: "A skilled, professional team assists in every shoot, ensuring smooth coordination from set to studio."
    }
  ];

  // Auto-scroll effect
  useEffect(() => {
    const interval = setInterval(() => {
      setScrollPosition(prev => {
        const newPosition = prev + 1;
        // Reset when reaching the end (assuming each card is ~400px wide)
        return newPosition >= 2000 ? 0 : newPosition;
      });
    }, 20);

    return () => clearInterval(interval);
  }, []);

  const scrollLeft = () => {
    setScrollPosition(prev => Math.max(0, prev - 400));
  };

  const scrollRight = () => {
    setScrollPosition(prev => prev + 400);
  };

  return (
    <section id="services" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex max-w-7xl mx-auto justify-between items-start mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <PillBadge text={"Why me?"} icon={''} className="mb-8" />
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl text-white leading-tight text-left"
            >
              <span className="text-[#999999]">I'll help your project </span>
              <span className="text-white">shine</span>
            </motion.h2>
          </motion.div>
        </div>

        {/* Services Horizontal Scroller with Controls */}
        <div className="relative mb-12 overflow-hidden">
          <motion.div
            className="flex gap-8 pr-8"
            animate={{ x: -scrollPosition }}
            transition={{ type: "tween", ease: "linear" }}
          >
            {[...services, ...services].map((service, index) => (
              <div
                key={index}
                className="w-80 sm:w-96 md:w-[400px] bg-[#0E0E0E] border border-[#333333] rounded-2xl p-6 sm:p-8 hover:bg-[#0f0f0f] transition-colors duration-300 flex-shrink-0"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-2xl border border-white/20 flex items-center justify-center text-white/90 text-lg">
                    {service.icon}
                  </div>
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-white mb-2 text-left">
                  {service.title}
                </h3>
                <p className="text-[#b3b3b3] text-left leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </motion.div>
          {/* Fade edges */}
          <div className="pointer-events-none absolute left-0 top-0 h-full w-12 bg-gradient-to-r from-black to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-black to-transparent" />

          {/* Controls */}
          <button
            type="button"
            onClick={scrollLeft}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full border border-white/20 bg-black/50 text-white hover:bg-black/70 transition"
            aria-label="Scroll left"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={scrollRight}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full border border-white/20 bg-black/50 text-white hover:bg-black/70 transition"
            aria-label="Scroll right"
          >
            ›
          </button>
        </div>

        {/* Contact Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          {/* <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          whileTap={{ scale: 0.95 }}
          whileHover="hover"
          className="bg-black border border-white/60 text-white px-6 py-3 rounded-full text-base font-medium hover:bg-white hover:text-black transition-all duration-300 flex items-center space-x-2 mx-auto mt-6"
        >
          <span>Contact</span>
          <span className="relative inline-flex w-4 h-4 overflow-hidden">
            <motion.svg
              className="absolute inset-0 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              variants={{ hover: { x: [0, 8], opacity: [1, 0] } }}
              transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7h-6M17 7v6" />
            </motion.svg>
            <motion.svg
              className="absolute inset-0 w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              initial={{ x: -8, opacity: 0 }}
              variants={{ hover: { x: [ -8, 0, -8 ], opacity: [0, 1, 0] } }}
              transition={{ duration: 1.0, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17L17 7M17 7h-6M17 7v6" />
            </motion.svg>
          </span>
        </motion.button> */}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;

